import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useCartWishlist } from '../../context/CartWishlistContext';
import { useLanguage } from '../../context/LanguageContext';
import { productAPI } from '../../api/product.api';
import { NAV_CATEGORIES } from '../../constants/catalog';
import { imageLooksUsable, normalizeImageUrl } from '../../utils/productImage';
import logoImage from '../../assets/sifrifana_logo.png';
import './NewHeader.css';

const normalizeSearchValue = (value) => String(value || '').trim().toLowerCase();
const getProductSpecialCode = (product = {}) => String(
  product.specialCode ||
  product.productCode ||
  product.code ||
  ''
).trim();

const productMatchesQuery = (product, query) => {
  const normalizedQuery = normalizeSearchValue(query);
  if (!normalizedQuery) {
    return false;
  }

  const searchableText = [
    product.title,
    product.name,
    product.brand,
    product.category,
    product.description,
    getProductSpecialCode(product),
  ].map(normalizeSearchValue).join(' ');

  return normalizedQuery.split(/\s+/).every((word) => searchableText.includes(word));
};

const sortProductsBySearch = (products, query) => {
  const normalizedQuery = normalizeSearchValue(query);

  return [...products].sort((first, second) => {
    const firstName = normalizeSearchValue(first.title || first.name);
    const secondName = normalizeSearchValue(second.title || second.name);
    const firstCode = normalizeSearchValue(getProductSpecialCode(first));
    const secondCode = normalizeSearchValue(getProductSpecialCode(second));
    const firstExactCode = firstCode === normalizedQuery ? 1 : 0;
    const secondExactCode = secondCode === normalizedQuery ? 1 : 0;
    const firstStartsWithQuery = firstName.startsWith(normalizedQuery) ? 1 : 0;
    const secondStartsWithQuery = secondName.startsWith(normalizedQuery) ? 1 : 0;

    return (
      secondExactCode - firstExactCode ||
      secondStartsWithQuery - firstStartsWithQuery ||
      firstName.localeCompare(secondName)
    );
  });
};

const getProductId = (product) => product.id || product._id;

const NewHeader = ({ onCartClick }) => {
  const { isAuthenticated } = useAuth();
  const { cartItems } = useCartWishlist();
  const { language, toggleLanguage, t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  const searchResults = React.useMemo(() => {
    if (searchQuery.trim().length < 2) {
      return [];
    }

    return sortProductsBySearch(
      products.filter((product) => productMatchesQuery(product, searchQuery)),
      searchQuery
    ).slice(0, 6);
  }, [products, searchQuery]);

  React.useEffect(() => {
    let isMounted = true;

    const loadProducts = async () => {
      try {
        const productsList = await productAPI.getAllAvailable();

        if (isMounted) {
          setProducts(productsList);
        }
      } catch (error) {
        console.error('Failed to load search products:', error);
        if (isMounted) {
          setProducts([]);
        }
      }
    };

    loadProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  const closeSearch = () => {
    setSearchOpen(false);
  };

  const openProduct = (product) => {
    const productId = getProductId(product);
    if (!productId) {
      return;
    }

    closeSearch();
    setSearchQuery('');
    navigate(`/products/${productId}`);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      return;
    }

    if (searchResults.length > 0) {
      openProduct(searchResults[0]);
      return;
    }

    closeSearch();
    navigate(`/products?search=${encodeURIComponent(trimmedQuery)}`);
  };

  return (
    <header className="header">
      <div className="header__topline">
        <div className="header__shell header__topline-inner">
          <div className="header__locale">
            <span className={language === 'en' ? 'is-active' : ''}>ENG</span>
            <button className="lang-toggle__switch" type="button" aria-label="Toggle language" onClick={toggleLanguage}>
              <span className="lang-toggle__knob" style={{ left: language === 'ka' ? '24px' : '3px' }} />
            </button>
            <span className={language === 'ka' ? 'is-active' : ''}>GEO</span>
          </div>
        </div>
      </div>

      <div className="header__mainbar">
        <div className="header__shell header__mainbar-inner">
          <Link to="/" className="brand" aria-label="Sifrifana home">
            <img src={logoImage} alt="Sifrifana" />
          </Link>

          <form className="search" action="#" method="get" onSubmit={handleSearchSubmit}>
            <input
              className="search__input"
              type="search"
              placeholder={t('common.search')}
              aria-label={t('common.searchProducts')}
              value={searchQuery}
              autoComplete="off"
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => window.setTimeout(closeSearch, 120)}
            />
            <button className="search__btn" type="submit" aria-label={t('common.search')}>
              <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
                <circle cx="13" cy="13" r="8.5" stroke="currentColor" strokeWidth="2.2" />
                <path d="M19.5 19.5L25 25" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>

            {searchOpen && searchQuery.trim().length >= 2 && (
              <div className="search-results" role="listbox" aria-label={t('common.searchProducts')}>
                {searchResults.length > 0 ? (
                  searchResults.map((product, index) => {
                    const productId = getProductId(product);
                    const productName = product.title || product.name || t('common.product');
                    const productCode = getProductSpecialCode(product);
                    const productImage = imageLooksUsable(product.image)
                      ? normalizeImageUrl(product.image)
                      : imageLooksUsable(product.images?.[0])
                        ? normalizeImageUrl(product.images[0])
                        : null;

                    return (
                      <button
                        key={productId || `${productName}-${index}`}
                        type="button"
                        className="search-results__item"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => openProduct(product)}
                      >
                        <span className="search-results__thumb">
                          {productImage ? <img src={productImage} alt="" /> : <span />}
                        </span>
                        <span className="search-results__copy">
                          <span className="search-results__title">{productName}</span>
                          <span className="search-results__meta">
                            {productCode ? `${t('product.specialCode')}: ${productCode}` : (product.brand || product.category || t('common.similarItem'))}
                          </span>
                        </span>
                      </button>
                    );
                  })
                ) : (
                  <div className="search-results__empty">{t('common.noSimilarProducts')}</div>
                )}
              </div>
            )}
          </form>

          <nav className="header-icons" aria-label="Header actions">
            <button className="header-icons__item" onClick={onCartClick} aria-label={t('header.cart')}>
              <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
                <path d="M5 7h3l3.2 15H25l2.3-9.5H12.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="14" cy="28" r="1.7" fill="currentColor" />
                <circle cx="24" cy="28" r="1.7" fill="currentColor" />
              </svg>
              {cartItems?.length > 0 && <span className="header-icons__badge">{cartItems.length}</span>}
            </button>
            <button className="header-icons__item" onClick={() => navigate(isAuthenticated ? '/user/wishlist' : '/login')} aria-label={t('header.wishlist')}>
              <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
                <path d="M17 28s-10.5-6.1-10.5-13.6c0-3.6 2.7-6.1 6.1-6.1 2.3 0 4.1 1 5.4 2.7 1.3-1.7 3.1-2.7 5.4-2.7 3.4 0 6.1 2.5 6.1 6.1C27.5 21.9 17 28 17 28Z" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="header-icons__item" onClick={() => navigate(isAuthenticated ? '/user/dashboard' : '/login')} aria-label={t('header.account')}>
              <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
                <circle cx="17" cy="11" r="5.5" stroke="currentColor" strokeWidth="2.2" />
                <path d="M7.5 27c1.8-4.1 5.2-6.2 9.5-6.2s7.7 2.1 9.5 6.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
          </nav>
        </div>
      </div>

      <div className="header__navrow">
        <div className="header__shell">
          <nav className="category-nav" aria-label="Main categories">
            {NAV_CATEGORIES.map((cat) => (
              <Link key={cat} className="category-nav__link" to={`/products?category=${cat}`}>
                {t(`category.${cat}`)}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default NewHeader;
